import { DEFAULT_STARTING_CREDITS, seedState } from './economy/state.js';
import type { EconomyState } from './economy/types.js';
import { mascotWithDefaults, toPublicMascot, type MascotSet, type PublicMascot } from './mascot.js';
import {
  globalLadderWithDefaults,
  roleIdsWithDefaults,
  rolesCatalogWithDefaults,
  seedGlobalLadder,
  type DiscordRoleOption,
  type GlobalTier,
} from './global.js';

/**
 * Model profiles — one creator's whole game inside a guild.
 *
 * **In the product and the UI a profile is called a _realm_.** The code keeps
 * `profile` because it is the name every save file, route and type already
 * carries; renaming it would be a migration for no behaviour. A realm is not a
 * Discord server — the server is the *guild*, and it hosts one or more realms.
 *
 * A guild no longer holds a single economy: it holds a **registry of profiles**,
 * and each profile owns a complete `EconomyState` of its own (wheels, players,
 * balances, orders — everything). Wallets are deliberately separate: 300 credits
 * with one creator are 0 with another, which is what makes revenue attribution
 * automatic instead of a negotiation.
 *
 * The guild owner's own game is not special-cased anywhere downstream: it is
 * simply the profile with the reserved id {@link OWNER_PROFILE_ID}, which also
 * maps onto the pre-profiles save file so an existing guild migrates by doing
 * nothing at all.
 */

/**
 * The colour a creator's whole game wears.
 *
 * Not decoration: a client who picks Yuna should feel he walked into *her*
 * place rather than a shared app with her name on it, and a clip filmed in the
 * studio should be recognisably hers before anyone reads a word. So the theme
 * follows the profile everywhere — console, studio and the player's game —
 * rather than being a studio setting.
 *
 * Deliberately a **closed list** rather than a free colour. Every theme has to
 * stay readable against the wheel's labels, the status badges and the reward
 * dialog; a colour picker would let someone produce an unreadable wheel and
 * only find out from a ruined video. Adding a fourth is a few lines in
 * `tokens.css` and one entry here.
 */
export const PROFILE_THEMES = ['spoyl', 'elf', 'vampire'] as const;

export type ProfileTheme = (typeof PROFILE_THEMES)[number];

export const DEFAULT_PROFILE_THEME: ProfileTheme = 'spoyl';

export function isValidProfileTheme(value: unknown): value is ProfileTheme {
  return typeof value === 'string' && (PROFILE_THEMES as readonly string[]).includes(value);
}

/**
 * Slice colours a theme suggests — **suggests**, never imposes.
 *
 * A wheel's colours are the owner's data: they carry meaning she put there
 * ("the big prize is the gold one"), and a theme that repainted them on load
 * would silently destroy a tuned wheel. So this palette is only ever applied by
 * an explicit click in the console, and it is reversible by another click.
 *
 * Ordered so consecutive slices contrast: the list is walked in order and
 * wrapped, so neighbours never share a colour unless the wheel has more slices
 * than the palette has entries.
 */
export const THEME_PALETTES: Record<ProfileTheme, string[]> = {
  spoyl: ['#8b5cf6', '#f43f5e', '#fbbf24', '#38bdf8', '#34d399', '#e879f9'],
  elf: ['#8e3cc3', '#d4af37', '#b9a7e8', '#2f9c9c', '#f7e7b0', '#c77dbb'],
  vampire: ['#9d0000', '#1a0e13', '#c0102a', '#2c2c2e', '#bfa181', '#5c0a14'],
};

/**
 * The words a theme suggests — **suggests**, exactly like {@link THEME_PALETTES}.
 *
 * A realm is a vocabulary before it is a colour: the Glade counts in Moonpetals
 * and measures Blessing, the Court counts in Bloodmarks and measures Corruption.
 * Typing thirty strings into a console under launch pressure is how a launch
 * ships half-dressed, so this is the whole set behind one button.
 *
 * Applied only by an explicit click and reversible by another, for the reason
 * the palettes are: these are the creator's data the moment she edits them, and
 * a theme that rewrote them on load would silently undo her work.
 *
 * `ladder` is read **in ascending threshold order** against whatever ladder the
 * profile actually has — names only, never thresholds or rewards. A ladder with
 * more rungs than this list keeps the extra ones' names.
 */
export interface ThemeNaming {
  creditName: string;
  creditNamePlural: string;
  xpName: string;
  /** Rung names, lowest first. The last is the hidden one. */
  ladder: string[];
}

export const THEME_NAMING: Record<ProfileTheme, ThemeNaming> = {
  spoyl: {
    creditName: 'Credit',
    creditNamePlural: 'Credits',
    xpName: 'XP',
    ladder: ['Member', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Black'],
  },
  elf: {
    creditName: 'Moonpetal',
    creditNamePlural: 'Moonpetals',
    xpName: 'Blessing',
    ladder: [
      'The Seedling',
      'The Petal',
      'The Bloomkeeper',
      'The Roseward',
      'The Archdruid',
      "The Elf's Chosen",
      // The hidden rung. Named from the Glade's own text — a name etched in its
      // heartwood is already what the top rank promises.
      'The Heartwood',
    ],
  },
  vampire: {
    creditName: 'Bloodmark',
    creditNamePlural: 'Bloodmarks',
    xpName: 'Corruption',
    ladder: [
      'The Stray',
      'The Vessel',
      'The Bloodbound',
      'The Crimson Knight',
      'The Veilkeeper',
      "The Vampire's Consort",
      // Likewise carved into the Court's chalice.
      'The Chalice',
    ],
  },
};

export interface ModelProfile {
  /** `owner` for the guild owner's own profile, otherwise minted server-side. */
  id: string;
  /** Player-facing name, e.g. "Yuna". What the picker shows. */
  name: string;
  /** The colour her whole game wears. See {@link ProfileTheme}. */
  theme: ProfileTheme;
  /**
   * Discord user id of the model who manages this profile, or null when only
   * the guild owner does. This is the whole of the manager grant: the server
   * compares it against the identity behind the bearer token, so nothing a
   * client claims about itself can reach it.
   */
  managerId: string | null;
  /**
   * Her Discord picture, remembered the last time she opened the app.
   *
   * Discord will not hand out an arbitrary user's avatar to a player's token —
   * only the token's own owner. So rather than reach for a bot token, which
   * would put the bot's credentials inside the one process facing the internet,
   * the profile *remembers*: whoever manages it is identified on every request
   * they make, and their picture is captured then. It follows a change of
   * avatar one session late, and stays null until she opens the app once.
   */
  avatar: string | null;
  /**
   * A picture set by hand in the console, which wins over {@link avatar}.
   *
   * Two reasons it exists: a model may want a better photo than her Discord
   * one, and the owner profile's captured avatar is whoever owns the guild —
   * not necessarily the creator it represents.
   */
  avatarOverride: string | null;
  /**
   * This realm's mascot: a small set of images, one per pose, dropped in by its
   * model or the owner (see `mascot.ts`). Null until she sets one, and every
   * reader falls back to a placeholder, so nothing needs art to work.
   *
   * Collectibles (owned items with a rarity, perhaps exportable as emojis or
   * stickers) are deliberately *not* modelled here. They belong to a guild and a
   * client rather than to one realm — see `collectibles.ts`.
   */
  mascot: MascotSet | null;
}

export interface ProfileRegistry {
  profiles: ModelProfile[];
  /**
   * The server-wide status ladder, driven by each client's total XP across
   * every realm in this guild — see `global.ts`.
   *
   * It lives on the registry because it belongs to the guild rather than to any
   * one realm: no realm's save may own a number that sums the others. A registry
   * saved before it existed is backfilled with the default ladder.
   */
  globalLadder: GlobalTier[];
  /**
   * The guild's Discord roles as the bot last reported them — what the console's
   * role picker offers. The Activity server holds no Discord credential, so this
   * is pushed by the bot through the rank bridge and only ever *read* here.
   */
  discordRoles: DiscordRoleOption[];
  /**
   * Roles that were once rungs and no longer are. The bot keeps removing them
   * from members, so deleting a rung (or swapping its role) cannot strand the old
   * role on everyone who held it. Never granted; never contains a role in use.
   */
  globalRetiredRoles: string[];
  /**
   * One-time data migrations already applied to this guild, by id. A migration
   * script records itself here so running it a second time is refused instead of
   * repeating it — the guard that makes a by-hand deploy step safe to re-run.
   */
  appliedMigrations: string[];
}

/** A profile as a player may see it: enough to pick one, nothing more. */
export interface PublicProfile {
  id: string;
  name: string;
  /** Her picture, override first. Null renders the initial instead. */
  avatar: string | null;
  /**
   * Her colour. Public on purpose — the player's game wears it too, and it
   * says nothing about her economy that the wheel doesn't already show.
   */
  theme: ProfileTheme;
  /** Her mascot art, as loadable URLs. Null renders the placeholder. */
  mascot: PublicMascot | null;
}

export const OWNER_PROFILE_ID = 'owner';

/**
 * Profile ids reach a filesystem path on the server, so like guild ids they are
 * validated as a closed shape rather than trusted: the reserved owner id, or a
 * minted `p…` id.
 */
export function isValidProfileId(id: string): boolean {
  return id === OWNER_PROFILE_ID || /^p[a-z0-9]{4,24}$/.test(id);
}

/** A brand-new registry: the owner's own profile and nobody else. */
export function seedRegistry(): ProfileRegistry {
  return {
    globalLadder: seedGlobalLadder(),
    discordRoles: [],
    globalRetiredRoles: [],
    appliedMigrations: [],
    profiles: [
      {
        id: OWNER_PROFILE_ID,
        name: 'Creator',
        theme: DEFAULT_PROFILE_THEME,
        managerId: null,
        avatar: null,
        avatarOverride: null,
        mascot: null,
      },
    ],
  };
}

/** The picture to show for a profile: what was set by hand, else what was seen. */
export function profileAvatar(profile: ModelProfile): string | null {
  return profile.avatarOverride ?? profile.avatar ?? null;
}

/**
 * Backfills a persisted registry, and guarantees the owner profile exists —
 * every guild has one, and the save file that predates profiles *is* it.
 */
export function registryWithDefaults(parsed: Partial<ProfileRegistry>): ProfileRegistry {
  const seed = seedRegistry();
  const profiles = (parsed.profiles ?? [])
    .filter((profile): profile is ModelProfile => {
      return (
        typeof profile === 'object' &&
        profile !== null &&
        typeof profile.id === 'string' &&
        isValidProfileId(profile.id)
      );
    })
    .map((profile) => ({
      id: profile.id,
      name: typeof profile.name === 'string' && profile.name ? profile.name : 'Creator',
      // A registry saved before themes existed simply wears the house colour.
      theme: isValidProfileTheme(profile.theme) ? profile.theme : DEFAULT_PROFILE_THEME,
      managerId: typeof profile.managerId === 'string' ? profile.managerId : null,
      // Both arrived with the picker's thumbnails; a registry saved before them
      // simply has no picture yet and renders an initial until she opens the app.
      avatar: typeof profile.avatar === 'string' && profile.avatar ? profile.avatar : null,
      avatarOverride:
        typeof profile.avatarOverride === 'string' && profile.avatarOverride
          ? profile.avatarOverride
          : null,
      // A registry from before sets existed holds null (or the first draft's bare
      // string); either upgrades without loss.
      mascot: mascotWithDefaults(profile.mascot),
    }));

  if (!profiles.some((profile) => profile.id === OWNER_PROFILE_ID)) {
    profiles.unshift(...seed.profiles);
  }
  return {
    profiles,
    globalLadder: globalLadderWithDefaults(parsed.globalLadder),
    discordRoles: rolesCatalogWithDefaults(parsed.discordRoles),
    globalRetiredRoles: roleIdsWithDefaults(parsed.globalRetiredRoles),
    appliedMigrations: Array.isArray(parsed.appliedMigrations)
      ? parsed.appliedMigrations.filter((id): id is string => typeof id === 'string')
      : [],
  };
}

export function findProfile(registry: ProfileRegistry, id: string): ModelProfile | null {
  return registry.profiles.find((profile) => profile.id === id) ?? null;
}

/** The profile this user manages, or null. One profile per manager, by design. */
export function managedProfileOf(registry: ProfileRegistry, userId: string): ModelProfile | null {
  return registry.profiles.find((profile) => profile.managerId === userId) ?? null;
}

/**
 * Strips the registry down to what a player may receive. No manager ids.
 *
 * `mascotPath` turns an uploaded file name into a path on this server, which is
 * the one thing that depends on the guild — so it is passed in rather than known
 * here.
 */
export function toPublicProfiles(
  registry: ProfileRegistry,
  mascotPath: (profileId: string, file: string) => string,
): PublicProfile[] {
  return registry.profiles.map((profile) => ({
    id: profile.id,
    name: profile.name,
    avatar: profileAvatar(profile),
    theme: profile.theme,
    mascot: toPublicMascot(profile.mascot, (file) => mascotPath(profile.id, file)),
  }));
}

/**
 * The economy a brand-new profile starts from: the base profile's current
 * wheels, reward tiers and settings (bar the starting balance) — and none of
 * its people. Players,
 * balances, history, orders and coupons all start empty, because a wallet
 * belongs to one creator's economy and never crosses into another.
 */
export function seedProfileFrom(base: EconomyState): EconomyState {
  return {
    ...seedState(),
    wheels: structuredClone(base.wheels),
    shop: structuredClone(base.shop),
    // A happy hour belongs to the economy that started it, not to a new one.
    progression: { ...structuredClone(base.progression), boost: null },
    // Everything but the starting balance. A realm's credits mirror what its
    // clients have actually done with *that* creator, so a new one must not hand
    // every arrival whatever the realm it was copied from happened to give — a
    // guild that once let a demo realm start people on 1,000 would otherwise
    // mint free spins for every client of every realm added after it. The owner
    // raises it per realm, on purpose, from that realm's console.
    settings: { ...base.settings, startingCredits: DEFAULT_STARTING_CREDITS },
  };
}
